import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { 
  Phone, MapPin, Star, Menu, X, ChevronRight,
  Coffee, Utensils, Wifi, Car, Trees, Flame,
  Home, Users, Calendar, Instagram, Navigation,
  Waves, Gamepad2, PartyPopper, Bird, Footprints, Droplets,
  Send, User, Mail, MessageSquare, Heart
} from "lucide-react";

// Constants
const WHATSAPP_NUMBER = "916360736941";
const CONTACT_NUMBER = "6360736941";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I would like to book a stay at Haranamakki Homestay.`;
const GOOGLE_MAPS_LINK = "https://maps.app.goo.gl/YJvzqKVzXhZ5F1ku9";
const INSTAGRAM_LINK = "https://www.instagram.com/horanadu_haranamakki_homestay_";

// Images
const IMAGES = {
  heroDesktop: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/zludhkv2_landing%20page%2033.jfif",
  heroMobile: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/6p9ryl5u_mobil%20view.jfif",
  glassHouse: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/wvw2kmi4_glass%20house%20evening.jfif",
  glassHouseDay: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/br36r9wz_glass%20house%20day.jfif",
  tentHouse: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/209xdbrd_tent%20house.jfif",
  malnadHome: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/zludhkv2_landing%20page%2033.jfif",
  campfire: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/9sofxwcq_campfire%202.jfif",
  candleDinner: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/hj66aro9_candle%20light%20dinner.jfif",
  pond: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/xmguwck2_sports.jfif",
  kayaking: "https://customer-assets.emergentagent.com/job_eb9d5e42-3be0-4e4a-986d-37e239d50ef7/artifacts/rneo587h_haranamakki3.jfif",
  food1: "https://customer-assets.emergentagent.com/job_eb9d5e42-3be0-4e4a-986d-37e239d50ef7/artifacts/f0ayfr07_haranamakki4.jfif",
  food2: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/s7zhail4_food%202.jfif",
  coffeeEstate: "https://customer-assets.emergentagent.com/job_eb9d5e42-3be0-4e4a-986d-37e239d50ef7/artifacts/clhnfkio_haranamakki%2002.jfif",
  homestayDay: "https://customer-assets.emergentagent.com/job_eb9d5e42-3be0-4e4a-986d-37e239d50ef7/artifacts/kc757se9_haranamakki5.jfif",
  outdoorGames: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/fjksty81_sports%204.jfif",
  carrom: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/1os4qiu9_carrom.jfif",
  welcome: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/qwrh57st_welcome%2011.jfif",
  nearbyPlaces: {
    kudremukha: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/hanz3049_kuduremukha.jfif",
    kalasa: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/yqw2m3gh_kalasa%20temple.jfif",
    falls: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/1wtvned6_hanumanagundi%20falls.jfif",
    temple: "https://customer-assets.emergentagent.com/job_jain-temple-stay/artifacts/qyz87qcl_horanadu%20temple.jfif"
  }
};

// Rooms data
const ROOMS = [
  {
    id: "glass-house",
    name: "Glass House",
    tagline: "Perfect for Couples",
    description: "Wake up to breathtaking mountain views and misty coffee estates. Our romantic glass house offers panoramic views of the Western Ghats.",
    features: ["Mountain Views", "Stargazing", "Private Space", "Coffee Estate View"],
    image: IMAGES.glassHouse,
    ideal: "Couples & Honeymooners"
  },
  {
    id: "tent-house",
    name: "Tent House",
    tagline: "Adventure Awaits",
    description: "Experience glamping at its finest! Our luxury tent offers an authentic camping experience with modern comforts amidst nature.",
    features: ["Starlit Nights", "Nature Sounds", "Cozy Bedding", "Adventure Experience"],
    image: IMAGES.tentHouse,
    ideal: "Adventurers & Nature Lovers"
  },
  {
    id: "malnad-home",
    name: "Malnad Home",
    tagline: "Traditional Heritage Stay",
    description: "Our spacious 3-bedroom traditional Malnad home is perfect for families and groups. Experience authentic Karnataka hospitality.",
    features: ["3 Bedrooms", "Family Friendly", "Traditional Architecture", "Spacious Living"],
    image: IMAGES.malnadHome,
    ideal: "Families & Friend Groups"
  }
];

// Experiences
const EXPERIENCES = [
  { icon: Coffee, title: "Coffee Estate Walk", description: "Stroll through aromatic coffee plantations" },
  { icon: Bird, title: "Bird Watching", description: "Spot exotic Western Ghats birds" },
  { icon: Footprints, title: "Nature Trails", description: "Explore scenic hiking paths" },
  { icon: Droplets, title: "Natural Waterfalls", description: "Visit nearby pristine waterfalls" },
  { icon: PartyPopper, title: "Event Hosting", description: "Birthday, Marriage & Office Parties" }
];

// Testimonials
const TESTIMONIALS = [
  { id: 1, name: "Priya Sharma", location: "Bangalore", rating: 5, text: "The glass house was magical - waking up to misty mountains was surreal. Best homestay in Kalasa!" },
  { id: 2, name: "Rajesh Kumar", location: "Mumbai", rating: 5, text: "Perfect anniversary trip! The candle light dinner and bonfire made it unforgettable." },
  { id: 3, name: "Ananya Reddy", location: "Chennai", rating: 5, text: "Brought 3 generations of family! Kids loved the pond activities. Amazing food and hospitality." },
  { id: 4, name: "Vikram Joshi", location: "Hyderabad", rating: 5, text: "The tent house experience was magical! Coffee walks, waterfalls, delicious food. Perfect escape!" }
];

// Nearby places
const NEARBY_PLACES = [
  { name: "Kudremukha Peak", image: IMAGES.nearbyPlaces.kudremukha, type: "Trekking" },
  { name: "Hanumangundi Falls", image: IMAGES.nearbyPlaces.falls, type: "Waterfall" },
  { name: "Annaporneshwari Temple Horanadu", image: IMAGES.nearbyPlaces.temple, type: "Temple" },
  { name: "Kalasa Temple", image: IMAGES.nearbyPlaces.kalasa, type: "Temple" }
];

// WhatsApp Icon
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Header
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Rooms", path: "/rooms" },
    { label: "Gallery", path: "/gallery" },
    { label: "Book Now", path: "/booking" }
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="font-logo text-lg md:text-xl text-primary-dark uppercase">
            Haranamakki Homestay
          </Link>
          
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map(item => (
              <Link 
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === item.path ? "text-primary" : "text-gray-700 hover:text-primary"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp flex items-center gap-2 text-sm py-2 px-4">
              <WhatsAppIcon className="w-4 h-4" />
              WhatsApp
            </a>
          </nav>

          <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(true)}>
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[200]" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed top-0 right-0 w-72 h-full bg-white z-[201] p-6 shadow-2xl">
            <button onClick={() => setMobileMenuOpen(false)} className="absolute top-4 right-4">
              <X size={24} />
            </button>
            <nav className="flex flex-col gap-4 mt-12">
              {navItems.map(item => (
                <Link 
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-medium text-gray-700 hover:text-primary py-2 border-b border-gray-100"
                >
                  {item.label}
                </Link>
              ))}
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp flex items-center justify-center gap-2 mt-4">
                <WhatsAppIcon className="w-5 h-5" />
                Book on WhatsApp
              </a>
            </nav>
          </div>
        </>
      )}
    </>
  );
};

// Hero Section with responsive background
const HeroSection = () => (
  <section className="hero-section">
    {/* Desktop Background */}
    <div 
      className="hero-bg hero-bg-desktop"
      style={{ backgroundImage: `url(${IMAGES.heroDesktop})` }}
    />
    {/* Mobile Background */}
    <div 
      className="hero-bg hero-bg-mobile"
      style={{ backgroundImage: `url(${IMAGES.heroMobile})` }}
    />
    <div className="absolute inset-0 hero-overlay" />
    
    <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-4 text-center">
      <h1 className="hero-title-main mb-4">
        <span className="block text-4xl sm:text-5xl md:text-7xl font-bold tracking-wide" style={{ fontFamily: "'Playfair Display', serif", textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
          HARANAMAKKI
        </span>
        <span className="block text-2xl sm:text-3xl md:text-4xl font-light tracking-[0.3em] mt-2 text-amber-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
          HOMESTAY
        </span>
      </h1>
      <p className="text-base md:text-xl text-white/90 mb-8 max-w-xl">
        Experience Nature Like Never Before
      </p>
      <div className="flex flex-wrap gap-3 justify-center hero-buttons">
        <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp flex items-center gap-2 text-sm">
          <WhatsAppIcon className="w-4 h-4" />
          Book Your Stay
        </a>
        <Link to="/rooms" className="btn-outline text-sm">
          Explore Rooms
        </Link>
      </div>
    </div>
  </section>
);

// Welcome Section - Guests with Nameboard
const WelcomeSection = () => (
  <section className="section-padding bg-gradient-to-br from-green-50 to-emerald-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="img-zoom rounded-2xl overflow-hidden shadow-xl">
          <img src={IMAGES.welcome} alt="Happy Guests at Haranamakki" className="w-full h-[400px] object-cover" />
        </div>
        <div>
          <p className="text-primary font-semibold text-sm tracking-widest mb-2">WELCOME</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            You're Home Here
          </h2>
          <div className="space-y-4 text-gray-600">
            <p>
              At <span className="font-semibold text-primary">Haranamakki Homestay</span>, every guest becomes family. Our warm hospitality and the natural beauty of Kalasa create memories that last a lifetime.
            </p>
            <p>
              From the moment you arrive at our welcoming gates to the tearful goodbyes, we ensure your stay is filled with joy, comfort, and the authentic Malnad experience.
            </p>
            <p className="font-semibold text-primary-dark">
              Join hundreds of happy guests who've made Haranamakki their home away from home!
            </p>
          </div>
          <div className="flex flex-wrap gap-4 mt-8">
            <div className="flex items-center gap-2 bg-light-green px-4 py-2 rounded-full">
              <Heart className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Warm Hospitality</span>
            </div>
            <div className="flex items-center gap-2 bg-light-green px-4 py-2 rounded-full">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Family Friendly</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section className="section-padding bg-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-primary font-semibold text-sm tracking-widest mb-2">ABOUT US</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Welcome to Paradise
          </h2>
          <div className="space-y-4 text-gray-600">
            <p>
              Nestled in the heart of Kalasa, Chikmagaluru district, our homestay offers an authentic Karnataka experience surrounded by lush coffee plantations and the majestic Western Ghats.
            </p>
            <p>
              Choose from our romantic <span className="text-primary font-semibold">Glass House</span>, adventurous <span className="text-primary font-semibold">Tent House</span>, or spacious <span className="text-primary font-semibold">Malnad Home</span> for families.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 mt-8">
            <div className="flex items-center gap-2 bg-light-green px-4 py-2 rounded-full">
              <Wifi className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Free WiFi</span>
            </div>
            <div className="flex items-center gap-2 bg-light-green px-4 py-2 rounded-full">
              <Car className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Free Parking</span>
            </div>
          </div>
        </div>
        <div className="img-zoom rounded-2xl overflow-hidden shadow-xl">
          <img src={IMAGES.coffeeEstate} alt="Coffee Estate" className="w-full h-[400px] object-cover" />
        </div>
      </div>
    </div>
  </section>
);

// Rooms Section
const RoomsSection = () => (
  <section className="section-padding bg-gradient-to-br from-gray-50 to-green-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-12">
        <p className="text-primary font-semibold text-sm tracking-widest mb-2">ACCOMMODATIONS</p>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800">
          Choose Your Stay
        </h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {ROOMS.map((room) => (
          <div key={room.id} className="room-card">
            <div className="relative h-56 img-zoom">
              <img src={room.image} alt={room.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="text-secondary text-xs font-semibold">{room.tagline}</p>
                <h3 className="font-heading text-xl font-bold">{room.name}</h3>
              </div>
            </div>
            <div className="p-5">
              <p className="text-gray-600 text-sm mb-4">{room.description}</p>
              <p className="text-xs text-gray-500 mb-4">Ideal for: {room.ideal}</p>
              <a 
                href={`${WHATSAPP_LINK}&text=Hi, I'm interested in booking the ${room.name}.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full text-center block text-sm py-2"
              >
                Book Now
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Campfire & Bonfire Section (below accommodations)
const CampfireSection = () => (
  <section className="section-padding bg-gradient-to-br from-orange-50 to-amber-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="img-zoom rounded-2xl overflow-hidden shadow-xl">
          <img src={IMAGES.campfire} alt="Campfire Night" className="w-full h-[350px] object-cover" />
        </div>
        <div>
          <p className="text-orange-500 font-semibold text-sm tracking-widest mb-2">EXPERIENCE</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Campfire & Bonfire Nights
          </h2>
          <p className="text-gray-600 mb-6">
            There's something magical about gathering around a crackling fire under the starlit sky. Share stories, roast marshmallows, and create unforgettable memories with your loved ones.
          </p>
          <ul className="space-y-3 mb-6">
            {["Cozy bonfire setup", "Perfect for groups", "Stargazing nights", "Music & storytelling"].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-700">
                <Flame className="w-4 h-4 text-orange-500" />
                {item}
              </li>
            ))}
          </ul>
          <a href={`${WHATSAPP_LINK}&text=Hi, I want to arrange a campfire/bonfire night.`} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2">
            <WhatsAppIcon className="w-4 h-4" />
            Book Campfire
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Candlelight Dinner Section
const CandlelightSection = () => (
  <section className="section-padding bg-gradient-to-br from-purple-50 to-pink-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="order-2 lg:order-1">
          <p className="text-purple-500 font-semibold text-sm tracking-widest mb-2">ROMANTIC</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Candlelight Dinner
          </h2>
          <p className="text-gray-600 mb-6">
            Create timeless memories with our exclusive candlelight dinner experience. Set amidst nature, with the soft glow of candles and authentic Malnad cuisine.
          </p>
          <ul className="space-y-3 mb-6">
            {["Private outdoor dining", "Romantic ambiance", "Special Malnad menu", "Perfect for couples"].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-700">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                {item}
              </li>
            ))}
          </ul>
          <a href={`${WHATSAPP_LINK}&text=Hi, I want to book a candlelight dinner.`} target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:shadow-lg transition">
            <WhatsAppIcon className="w-4 h-4" />
            Book Dinner
          </a>
        </div>
        <div className="order-1 lg:order-2 img-zoom rounded-2xl overflow-hidden shadow-xl">
          <img src={IMAGES.candleDinner} alt="Candlelight Dinner" className="w-full h-[350px] object-cover" />
        </div>
      </div>
    </div>
  </section>
);

// Water Activities Section
const WaterActivitiesSection = () => (
  <section className="section-padding bg-gradient-to-br from-blue-50 to-cyan-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-blue-600 font-semibold text-sm tracking-widest mb-2">WATER ACTIVITIES</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Natural Pond & Kayaking
          </h2>
          <div className="space-y-4 text-gray-600">
            <p>
              Discover our beautiful <span className="font-semibold text-blue-600">natural pond</span> nestled right inside our coffee estate. This serene water body is surrounded by lush greenery and creates the perfect setting for unforgettable water activities.
            </p>
            <p>
              Experience the <span className="font-semibold text-blue-600">thrill of paddle boating</span> as you glide across the calm waters, surrounded by the sounds of nature. Our kayaking sessions offer both beginners and experienced paddlers an exciting adventure amidst the coffee plantations.
            </p>
          </div>
          <ul className="space-y-3 mt-6 mb-8">
            {["Thrilling Paddle Boating", "Kayaking Adventures", "Swimming (Seasonal)", "Scenic Nature Views"].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-700">
                <Waves className="w-5 h-5 text-blue-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="img-zoom rounded-xl overflow-hidden shadow-lg">
            <img src={IMAGES.pond} alt="Natural Pond" className="w-full h-48 object-cover" />
          </div>
          <div className="img-zoom rounded-xl overflow-hidden shadow-lg mt-8">
            <img src={IMAGES.kayaking} alt="Kayaking" className="w-full h-48 object-cover" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Experiences Section
const ExperiencesSection = () => (
  <section className="section-padding bg-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-12">
        <p className="text-primary font-semibold text-sm tracking-widest mb-2">EXPERIENCES</p>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800">
          Unforgettable Moments
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {EXPERIENCES.map((exp, index) => (
          <div key={index} className="feature-card text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
              <exp.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm mb-1">{exp.title}</h3>
            <p className="text-xs text-gray-500">{exp.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Games Section with images
const GamesSection = () => (
  <section className="section-padding bg-gradient-to-br from-orange-50 to-amber-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-12">
        <p className="text-secondary font-semibold text-sm tracking-widest mb-2">RECREATION</p>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800">
          Games & Activities
        </h2>
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        {/* Indoor Games */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
          <div className="img-zoom h-48">
            <img src={IMAGES.carrom} alt="Indoor Games - Carrom" className="w-full h-full object-cover" />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-800">Indoor Games</h3>
            </div>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-purple-500 rounded-full" /> Chess - Strategic board game</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-purple-500 rounded-full" /> Carrom - Classic family fun</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-purple-500 rounded-full" /> Other board games available</li>
            </ul>
          </div>
        </div>
        {/* Outdoor Games */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
          <div className="img-zoom h-48">
            <img src={IMAGES.outdoorGames} alt="Outdoor Games - Volleyball" className="w-full h-full object-cover" />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Trees className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-800">Outdoor Games</h3>
            </div>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Volleyball - Team sports</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Cricket - Play in nature</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Other outdoor activities</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Food Section with two images
const FoodSection = () => (
  <section className="section-padding bg-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="grid grid-cols-2 gap-4">
          <div className="img-zoom rounded-2xl overflow-hidden shadow-xl">
            <img src={IMAGES.food1} alt="Malnad Food" className="w-full h-52 object-cover" />
          </div>
          <div className="img-zoom rounded-2xl overflow-hidden shadow-xl mt-8">
            <img src={IMAGES.food2} alt="Malnad Snacks" className="w-full h-52 object-cover" />
          </div>
        </div>
        <div>
          <p className="text-secondary font-semibold text-sm tracking-widest mb-2">CUISINE</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Authentic Malnad Food
          </h2>
          <p className="text-gray-600 mb-6">
            Savor the authentic flavors of Malnad cuisine prepared with fresh local ingredients. Our traditional recipes have been passed down through generations.
          </p>
          <ul className="space-y-2 mb-6 text-gray-700">
            {["Traditional Malnad Thali", "Fresh Estate Coffee", "Homemade Pickles & Chutneys", "Local Delicacies"].map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                <Utensils className="w-4 h-4 text-secondary" />
                {item}
              </li>
            ))}
          </ul>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700 font-semibold">🚫 Strictly No Pork</p>
            <p className="text-red-600 text-sm">We serve pure vegetarian and non-vegetarian Malnad cuisine (No Pork)</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => (
  <section className="section-padding bg-gradient-to-br from-green-50 to-emerald-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-12">
        <p className="text-primary font-semibold text-sm tracking-widest mb-2">TESTIMONIALS</p>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800">
          What Our Guests Say
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {TESTIMONIALS.map((testimonial) => (
          <div key={testimonial.id} className="bg-white p-6 rounded-2xl shadow-md">
            <div className="flex gap-1 mb-3">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-gray-600 text-sm mb-4">"{testimonial.text}"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                {testimonial.name.charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-sm text-gray-800">{testimonial.name}</p>
                <p className="text-xs text-gray-500">{testimonial.location}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Nearby Places Section
const NearbyPlacesSection = () => (
  <section className="section-padding bg-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-12">
        <p className="text-primary font-semibold text-sm tracking-widest mb-2">EXPLORE</p>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800">
          Nearby Attractions
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {NEARBY_PLACES.map((place, index) => (
          <a
            key={index}
            href={GOOGLE_MAPS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative h-48 rounded-xl overflow-hidden cursor-pointer card-hover"
          >
            <img src={place.image} alt={place.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3 text-white">
              <p className="text-secondary text-xs font-semibold">{place.type}</p>
              <h3 className="font-semibold text-sm">{place.name}</h3>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

// Booking Form Section (on homepage)
const BookingFormSection = () => {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', checkIn: '', checkOut: '', guests: '', roomType: '', message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `Hi, I want to book a stay:\n\nName: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nCheck-in: ${formData.checkIn}\nCheck-out: ${formData.checkOut}\nGuests: ${formData.guests}\nRoom: ${formData.roomType}\nMessage: ${formData.message}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <section className="section-padding bg-gradient-to-br from-green-50 to-emerald-50">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-10">
          <p className="text-primary font-semibold text-sm tracking-widest mb-2">RESERVATIONS</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800">
            Book Your Stay
          </h2>
          <p className="text-gray-500 mt-2">Fill the form and we'll get back to you on WhatsApp</p>
        </div>
        <div className="booking-form">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="text" className="form-input pl-10" placeholder="Your name" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="tel" className="form-input pl-10" placeholder="Your phone" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="email" className="form-input pl-10" placeholder="Your email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="date" className="form-input pl-10" required value={formData.checkIn} onChange={(e) => setFormData({...formData, checkIn: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="date" className="form-input pl-10" required value={formData.checkOut} onChange={(e) => setFormData({...formData, checkOut: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests *</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="number" className="form-input pl-10" placeholder="Guests" min="1" required value={formData.guests} onChange={(e) => setFormData({...formData, guests: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Type *</label>
                <div className="relative">
                  <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select className="form-input pl-10" required value={formData.roomType} onChange={(e) => setFormData({...formData, roomType: e.target.value})}>
                    <option value="">Select room</option>
                    <option value="Glass House">Glass House</option>
                    <option value="Tent House">Tent House</option>
                    <option value="Malnad Home">Malnad Home (3BR)</option>
                  </select>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea className="form-input pl-10 min-h-[80px]" placeholder="Any special requests..." value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
              </div>
            </div>
            <button type="submit" className="btn-whatsapp w-full flex items-center justify-center gap-2 py-3">
              <Send className="w-5 h-5" />
              Send Booking Request via WhatsApp
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => (
  <section className="section-padding bg-primary text-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <p className="text-white/70 font-semibold text-sm tracking-widest mb-2">CONTACT US</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to Experience Paradise?
          </h2>
          <div className="space-y-4 mb-8">
            <a href={`tel:${CONTACT_NUMBER}`} className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Call Us</p>
                <p className="font-semibold">{CONTACT_NUMBER}</p>
              </div>
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-xl bg-[#25D366]/30 flex items-center justify-center group-hover:bg-[#25D366]/50 transition">
                <WhatsAppIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/60 text-sm">WhatsApp</p>
                <p className="font-semibold">{CONTACT_NUMBER}</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Location</p>
                <p className="font-medium">Kalasa, Chikmagaluru, Karnataka</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp flex items-center gap-2 text-sm">
              <WhatsAppIcon className="w-4 h-4" />
              WhatsApp
            </a>
            <a href={`tel:${CONTACT_NUMBER}`} className="btn-secondary flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4" />
              Call Now
            </a>
            <a href={GOOGLE_MAPS_LINK} target="_blank" rel="noopener noreferrer" className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition">
              <Navigation className="w-4 h-4" />
              Directions
            </a>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <p className="text-white/60 text-sm">Follow Us:</p>
            <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center hover:scale-110 transition">
              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl h-[350px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3878.5!2d75.4083!3d13.4167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbab8e5dd96be73%3A0x45eb09c7c8c7c5e5!2sHornadu%2C%20Karnataka%20577181!5e0!3m2!1sen!2sin!4v1703000000000!5m2!1sen!2sin"
            className="w-full h-full border-0"
            loading="lazy"
            title="Location Map"
          />
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-gray-900 text-white py-8">
    <div className="max-w-6xl mx-auto px-4">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="font-logo text-lg uppercase">Haranamakki Homestay</p>
        <p className="text-gray-400 text-sm">© {new Date().getFullYear()} All rights reserved.</p>
        <div className="flex gap-4">
          <a href={`tel:${CONTACT_NUMBER}`} className="text-gray-400 hover:text-white transition">
            <Phone size={20} />
          </a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#25D366] transition">
            <WhatsAppIcon className="w-5 h-5" />
          </a>
          <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500 transition">
            <Instagram size={20} />
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// Floating Buttons
const FloatingButtons = () => (
  <>
    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="floating-btn whatsapp-float">
      <WhatsAppIcon className="w-7 h-7" />
    </a>
    <a href={`tel:${CONTACT_NUMBER}`} className="floating-btn call-float">
      <Phone className="w-6 h-6" />
    </a>
  </>
);

// Page Components
const HomePage = () => (
  <div>
    <HeroSection />
    <WelcomeSection />
    <AboutSection />
    <RoomsSection />
    <CampfireSection />
    <CandlelightSection />
    <WaterActivitiesSection />
    <ExperiencesSection />
    <GamesSection />
    <FoodSection />
    <TestimonialsSection />
    <NearbyPlacesSection />
    <BookingFormSection />
    <ContactSection />
  </div>
);

// Rooms Page
const RoomsPage = () => (
  <div className="pt-20">
    <div className="page-header">
      <h1 className="font-logo text-3xl md:text-5xl tracking-wide mb-2 uppercase">Our Rooms</h1>
      <p className="text-white/80">Find Your Perfect Stay</p>
    </div>
    <RoomsSection />
    <section className="section-padding bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-primary font-semibold text-sm tracking-widest mb-2">GALLERY</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-800">
            Room Gallery
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[IMAGES.glassHouse, IMAGES.glassHouseDay, IMAGES.tentHouse, IMAGES.malnadHome, IMAGES.homestayDay, IMAGES.carrom].map((img, i) => (
            <div key={i} className="img-zoom rounded-xl overflow-hidden shadow-md aspect-square">
              <img src={img} alt={`Room ${i+1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </section>
    <ContactSection />
  </div>
);

// Gallery Page
const GalleryPage = () => (
  <div className="pt-20">
    <div className="page-header">
      <h1 className="font-logo text-3xl md:text-5xl tracking-wide mb-2 uppercase">Gallery</h1>
      <p className="text-white/80">Glimpses of Paradise</p>
    </div>
    <section className="section-padding bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            IMAGES.heroDesktop, IMAGES.glassHouse, IMAGES.glassHouseDay, 
            IMAGES.tentHouse, IMAGES.malnadHome, IMAGES.campfire,
            IMAGES.candleDinner, IMAGES.pond, IMAGES.kayaking,
            IMAGES.food1, IMAGES.food2, IMAGES.coffeeEstate,
            IMAGES.outdoorGames, IMAGES.carrom, IMAGES.welcome, IMAGES.homestayDay
          ].map((img, i) => (
            <div key={i} className="img-zoom rounded-xl overflow-hidden shadow-md aspect-square">
              <img src={img} alt={`Gallery ${i+1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </section>
    <ContactSection />
  </div>
);

// Booking Page
const BookingPage = () => {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', checkIn: '', checkOut: '', guests: '', roomType: '', message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `Hi, I want to book a stay:\n\nName: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nCheck-in: ${formData.checkIn}\nCheck-out: ${formData.checkOut}\nGuests: ${formData.guests}\nRoom: ${formData.roomType}\nMessage: ${formData.message}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="pt-20">
      <div className="page-header">
        <h1 className="font-logo text-3xl md:text-5xl tracking-wide mb-2 uppercase">Book Your Stay</h1>
        <p className="text-white/80">Reserve Your Perfect Getaway</p>
      </div>
      <section className="section-padding bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="booking-form">
            <h2 className="font-heading text-2xl font-bold text-gray-800 mb-6 text-center">Booking Request</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" className="form-input pl-10" placeholder="Your name" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="tel" className="form-input pl-10" placeholder="Your phone" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="email" className="form-input pl-10" placeholder="Your email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="date" className="form-input pl-10" required value={formData.checkIn} onChange={(e) => setFormData({...formData, checkIn: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="date" className="form-input pl-10" required value={formData.checkOut} onChange={(e) => setFormData({...formData, checkOut: e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests *</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="number" className="form-input pl-10" placeholder="Guests" min="1" required value={formData.guests} onChange={(e) => setFormData({...formData, guests: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Type *</label>
                  <div className="relative">
                    <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select className="form-input pl-10" required value={formData.roomType} onChange={(e) => setFormData({...formData, roomType: e.target.value})}>
                      <option value="">Select room</option>
                      <option value="Glass House">Glass House</option>
                      <option value="Tent House">Tent House</option>
                      <option value="Malnad Home">Malnad Home (3BR)</option>
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea className="form-input pl-10 min-h-[100px]" placeholder="Any special requests..." value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
                </div>
              </div>
              <button type="submit" className="btn-whatsapp w-full flex items-center justify-center gap-2 py-3">
                <Send className="w-5 h-5" />
                Send Booking Request via WhatsApp
              </button>
            </form>
            <p className="text-center text-gray-500 text-sm mt-4">
              Your booking request will be sent to our WhatsApp for confirmation
            </p>
          </div>
        </div>
      </section>
      <ContactSection />
    </div>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <div className="min-h-screen">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/rooms" element={<RoomsPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/booking" element={<BookingPage />} />
        </Routes>
        <Footer />
        <FloatingButtons />
      </div>
    </BrowserRouter>
  );
}

export default App;
